<template>
  <div class="ppt">
    <div class="ppt-filters">
      <div class="ppt-filters__chips">
        <button
          v-for="chip in filterChips"
          :key="chip.key"
          type="button"
          class="ppt-chip"
          @click="$emit('remove-filter', chip.key)"
        >
          <span>{{ chip.label }}</span>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M15 5L5 15M5 5l10 10"
              stroke="#344054"
              stroke-width="1.67"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="ppt-visually-hidden">Remove {{ chip.label }} filter</span>
        </button>

        <button type="button" class="ppt-chip ppt-chip--more" @click="$emit('open-more-filters')">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M2.5 5h15M5.833 10h8.334M8.333 15h3.334"
              stroke="#344054"
              stroke-width="1.67"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span>More filters</span>
        </button>
      </div>

      <div class="ppt-search">
        <SearchPayPlansTableByMultipleFieldBox
          ref="searchRef"
          :search_instructions="searchPlaceholder"
          :field_name="searchFields"
          @filters-changed="onSearchFiltersChanged"
        />
      </div>
    </div>

    <div v-if="asOfEnabled" class="ppt-asof">
      <AsOfDateNavigator
        :today="asOfIsToday"
        :as-of-date="asOfDate"
        @reset-to-today="emit('reset-as-of')"
        @open-date-picker="emit('open-as-of-picker')"
      />
    </div>

    <div class="ppt-table-wrap">
      <div v-if="loading" class="ppt-loading" role="status" aria-live="polite">
        <template v-if="skeletonEnabled">
          <div
            v-for="n in 5"
            :key="n"
            class="ppt-skeleton-row"
          >
            <v-skeleton-loader type="text" class="ppt-skeleton" />
            <v-skeleton-loader type="text" class="ppt-skeleton" />
            <v-skeleton-loader type="text" class="ppt-skeleton" />
            <v-skeleton-loader type="text" class="ppt-skeleton" />
          </div>
        </template>
        <ExLoader v-else text="Loading..." />
      </div>

      <div v-else-if="error" class="ppt-error" role="alert">
        <p class="ppt-error__title">Failed to load pay plans</p>
        <p class="ppt-error__text">{{ errorText }}</p>
        <button type="button" class="ppt-error__retry" @click="$emit('retry')">
          Retry
        </button>
      </div>

      <div v-else-if="noPayRanges" class="ppt-no-ranges">
        <div class="ppt-no-ranges__content">
          <EmptyState
            title="Set up pay ranges first"
            :supporting-text="NO_PAY_RANGES_TEXT"
          >
            <template #actions>
              <button type="button" class="ppt-empty-cta" @click="emit('go-to-pay-ranges')">
                Go to Pay Ranges
              </button>
            </template>
          </EmptyState>
        </div>
      </div>

      <table v-else class="ppt-table">
        <colgroup>
          <col class="ppt-col-employee" />
          <col class="ppt-col-pay-plan" />
          <col class="ppt-col-changes" />
          <col class="ppt-col-status" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Employee</th>
            <th scope="col">
              <span class="ppt-th-with-icon">
                <img
                  class="ppt-th-icon"
                  :src="bankNoteIcon"
                  alt=""
                  aria-hidden="true"
                />
                Current Pay Plan
              </span>
            </th>
            <th scope="col">Proposed Increases</th>
            <th scope="col" class="ppt-th-status">{{ statusHeader }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.id"
            :class="{ 'ppt-row--clickable': rowHasEmployee(row) }"
            @click="onRowClick(row)"
          >
            <td>
              <div class="ppt-employee-cell">
                <EmployeeCard
                  v-if="rowHasEmployee(row)"
                  :first-name="row.employee.firstName"
                  :last-name="row.employee.lastName"
                  :position="row.employee.positionTitle || ''"
                  :department="row.employee.department || ''"
                  :photo-url="row.employee.avatarUrl"
                  :inactive="Boolean(row.employee.inactive)"
                  :availability="row.employee.availability"
                  :is-allocated="Boolean(row.employee.isAllocated)"
                  :hide-availability-selector="true"
                  :show-availability="row.employee.showAvailability !== false"
                  :show-importance-selector="false"
                  :is-hover-enabled="false"
                  :show-link-underline="false"
                  text-column-max-width="180px"
                />
              </div>
            </td>
            <td>
              <div class="ppt-pay-plan-cell" @click.stop>
                <PayPlanVersionSlider
                  v-if="sliderVersions(row).length"
                  :versions="sliderVersions(row)"
                  :blocked="blockedFor(row)"
                  @add-click="onAddPayPlan(row)"
                  @plan-click="(version) => onOpenPayPlan(row, version)"
                  @version-change="(version) => emit('as-of-version-change', row, version)"
                  @close-click="$emit('remove-pay-plan', row)"
                  @blocked-open="(blocked) => onOpenBlockingItem(row, blocked)"
                />
              </div>
            </td>
            <td>
              <div class="ppt-changes-cell" @click.stop>
                <ChangePayCardSmall
                  v-if="row.changes"
                  v-bind="row.changes"
                  :blocked="blockedFor(row)"
                  @button-click="onChangesButtonClick(row)"
                  @propose-first-plan="onProposeFirstPlan(row)"
                  @propose-pay-plan="onProposePayPlan(row)"
                  @propose-changes="onProposeChanges(row)"
                  @evaluate-eligibility="onEvaluateEligibility(row)"
                  @click="onChangeCardClick(row)"
                  @close-click="$emit('remove-change', row)"
                  @void="$emit('void-change', row)"
                  @blocked-open="(blocked) => onOpenBlockingItem(row, blocked)"
                />
              </div>
            </td>
            <td>
              <div class="ppt-status-cell">
                <PayRangePenetrationBar
                  v-if="row.readinessEnabled === false"
                  :range-penetration="row.rangePenetration"
                  :currency-symbol="row.currencySymbol"
                />
                <template v-else-if="row.fairPayStatus && row.fairPayStatus.status">
                  <FairPayStatusShort
                    :status="row.fairPayStatus.status"
                    :amount="row.fairPayStatus.amount"
                    :currency-code="row.fairPayStatus.currencyCode || 'USD'"
                  />
                  <p
                    v-if="row.fairPayStatus.readinessPct != null"
                    class="ppt-readiness"
                  >
                    Readiness {{ row.fairPayStatus.readinessPct }}%
                  </p>
                </template>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="!loading && !error && !noPayRanges && !rows.length" class="ppt-empty">
        <EXEmptyState
          :title="emptyTitle"
          :supporting-text="emptyMessage"
          :action-label="showEmptyAddPayPlan ? 'Add Pay Plan' : ''"
          title-class="ppt-empty__title"
          supporting-text-class="ppt-empty__subtitle"
          action-class="ppt-empty-cta"
          @action="onEmptyAddPayPlan"
        />
      </div>
    </div>

    <div v-if="pageCount > 1 && !noPayRanges" class="ppt-pagination">
      <button type="button" class="ppt-page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M15.833 10H4.167M4.167 10 10 15.833M4.167 10 10 4.167"
            stroke="#344054"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Previous
      </button>

      <div class="ppt-page-numbers">
        <span v-for="item in pageItems" :key="item.key">
          <span v-if="item.ellipsis" class="ppt-page-num ppt-page-num--ellipsis">...</span>
          <button
            v-else
            type="button"
            class="ppt-page-num"
            :class="{ 'ppt-page-num--active': item.page === page }"
            :aria-current="item.page === page ? 'page' : null"
            :aria-label="`Page ${item.page}`"
            @click="goToPage(item.page)"
          >
            {{ item.page }}
          </button>
        </span>
      </div>

      <button
        type="button"
        class="ppt-page-btn"
        :disabled="page >= pageCount"
        @click="goToPage(page + 1)"
      >
        Next
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            d="M4.167 10h11.666M15.833 10 10 4.167M15.833 10 10 15.833"
            stroke="#344054"
            stroke-width="1.67"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref } from "vue";

import AsOfDateNavigator from "@components/AsOfDateNavigator/AsOfDateNavigator.vue";
import ChangePayCardSmall from "@components/ChangePayCardSmall/ChangePayCardSmall.vue";
import EmployeeCard from "@components/organizations/employees/EmployeeCard/EmployeeCard.vue";
import EmptyState from "@components/EmptyState/EmptyState.vue";
import EXEmptyState from "@components/EXEmptyState/EXEmptyState.vue";
import ExLoader from "@components/common/ExLoader.vue";
import FairPayStatusShort from "@components/FairPayStatusShort/FairPayStatusShort.vue";
import PayRangePenetrationBar from "@components/PayRangePenetrationBar/PayRangePenetrationBar.vue";
import PayPlanVersionSlider from "@components/PayPlanVersionSlider/PayPlanVersionSlider.vue";
import SearchPayPlansTableByMultipleFieldBox from "@components/SearchPayPlansTableByMultipleFieldBox/SearchPayPlansTableByMultipleFieldBox.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import { PAY_PLANS_SEARCH_FIELDS } from "./payPlansSearchFields.js";
import { hasEmployeeIdentity } from "./mapPayPlanListRow.js";
import bankNoteIcon from "./icons/bank-note-01.svg";

const props = defineProps({
  rows: {
    type: Array,
    default: () => [],
  },
  filterChips: {
    type: Array,
    default: () => [],
  },
  searchPlaceholder: {
    type: String,
    default: "Search by Employee, Position",
  },
  searchFields: {
    type: Array,
    default: () => PAY_PLANS_SEARCH_FIELDS,
  },
  page: {
    type: Number,
    default: 1,
  },
  pageCount: {
    type: Number,
    default: 1,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  /**
   * True/string message shown in place of the table when the list fetch fails
   * (BDD scenario 6). A plain `true` falls back to the default copy.
   */
  error: {
    type: [Boolean, String],
    default: false,
  },
  /** Shows the "As of" date control (idea-MDP-429). */
  asOfEnabled: {
    type: Boolean,
    default: false,
  },
  /** ISO date (YYYY-MM-DD) the list is resolved against. */
  asOfDate: {
    type: String,
    default: "",
  },
  /** True when the list is resolved against today (no custom date). */
  asOfIsToday: {
    type: Boolean,
    default: true,
  },
  /** No In Force pay range as of today: replaces the rows and pagination with the first-run empty state. */
  noPayRanges: {
    type: Boolean,
    default: false,
  },
  /** ER-12691 (idea-MDP-429): show the API-reported "Not allowed" reason on blocked rows. */
  blockedReasonEnabled: {
    type: Boolean,
    default: false,
  },
  /** Empty-state copy (ER-12850). The employee-scoped list overrides the title/CTA. */
  emptyTitle: {
    type: String,
    default: "No pay plans yet",
  },
  emptyMessage: {
    type: String,
    default: "",
  },
  /** Shows the Add Pay Plan CTA in the empty state when the viewer may create plans. */
  canAddPayPlan: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits([
  "update:page",
  "filters-changed",
  "remove-filter",
  "open-more-filters",
  "add-pay-plan",
  "remove-pay-plan",
  "open-pay-plan",
  "changes-button-click",
  "propose-first-plan",
  "propose-pay-plan",
  "propose-changes",
  "evaluate-eligibility",
  "remove-change",
  "void-change",
  "open-proposal",
  "open-blocking-item",
  "row-click",
  "retry",
  "open-as-of-picker",
  "reset-as-of",
  "as-of-version-change",
  "go-to-pay-ranges",
]);

// Rule A-40: with Readiness off the column shows the position in the range, not a Fair Pay status.
const statusHeader = computed(() =>
  props.rows.some((row) => row.readinessEnabled === false)
    ? "Position in range"
    : "Current Fair Pay Status"
);

const showEmptyAddPayPlan = computed(() => props.canAddPayPlan);

const NO_PAY_RANGES_TEXT =
  "Pay plans are checked against the pay range of the employee's position. Add a pay range, subscribe your positions to it, then add pay plans here.";

const errorText = computed(() =>
  typeof props.error === "string" && props.error
    ? props.error
    : "Something went wrong while loading the pay plans. Please try again."
);

// MDP-560 — gate the skeleton grid behind the consistent-skeleton feature flag.
// Flag off (or unresolved) uses the shared ExLoader overlay.
const app = getCurrentInstance();
const { proxy } = app || {};
const skeletonEnabled = ref(false);

onMounted(checkSkeletonFlag);

async function checkSkeletonFlag() {
  try {
    skeletonEnabled.value =
      (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
  } catch {
    skeletonEnabled.value = false;
  }
}

function rowHasEmployee(row) {
  return hasEmployeeIdentity(row);
}

function onRowClick(row) {
  if (!rowHasEmployee(row)) return;
  emit("row-click", row);
}

function onAddPayPlan(row) {
  emit("add-pay-plan", row);
}

/** Empty-state CTA: no row is in context, so the consumer opens its create flow. */
function onEmptyAddPayPlan() {
  emit("add-pay-plan", null);
}

function onOpenPayPlan(row, version = null) {
  emit("open-pay-plan", row, version);
}

/**
 * The slider's versions for a row. Rows built by `mapPayPlanListRow` carry
 * `payPlanVersions`; fall back to the single current version so the cell still
 * renders for callers that pass the pre-slider row shape.
 */
function sliderVersions(row) {
  if (Array.isArray(row?.payPlanVersions) && row.payPlanVersions.length) {
    return row.payPlanVersions;
  }
  return row?.currentPayPlan ? [row.currentPayPlan] : [];
}

function blockedFor(row) {
  return props.blockedReasonEnabled ? row?.blocked || null : null;
}

function onOpenBlockingItem(row, blocked) {
  emit("open-blocking-item", row, blocked);
}

function onProposeFirstPlan(row) {
  emit("propose-first-plan", row);
}

function onProposePayPlan(row) {
  emit("propose-pay-plan", row);
}

function onProposeChanges(row) {
  emit("propose-changes", row);
}

function onEvaluateEligibility(row) {
  emit("evaluate-eligibility", row);
}

function onChangeCardClick(row) {
  // The change card is a proposal (not a CTA button / evaluation) — open the
  // proposal drawer for that PR- record.
  if (row?.changes?.mode === "proposal") {
    emit("open-proposal", row);
  }
}

function onChangesButtonClick(row) {
  const button = row?.changes?.button;
  if (button === "propose-first-pay-plan") {
    emit("propose-first-plan", row);
    return;
  }
  if (button === "propose-pay-plan") {
    emit("propose-pay-plan", row);
    return;
  }
  // Propose Changes / Evaluate Eligibility use dedicated events above.
  // Do not map them here — button-click also fires for Evaluate Eligibility
  // while row.changes.button is still "propose-changes".
  emit("changes-button-click", row);
}

function buildPageItems(current, total) {
  if (total <= 0) return [];
  const keep = new Set(
    [1, 2, 3, total - 2, total - 1, total, current - 1, current, current + 1].filter(
      (p) => p >= 1 && p <= total
    )
  );
  const sorted = [...keep].sort((a, b) => a - b);
  const items = [];
  let previous = 0;
  sorted.forEach((p) => {
    if (p - previous > 1) {
      items.push({ key: `ellipsis-${p}`, ellipsis: true });
    }
    items.push({ key: `page-${p}`, page: p });
    previous = p;
  });
  return items;
}

const pageItems = computed(() => buildPageItems(props.page, props.pageCount));

function goToPage(next) {
  if (next < 1 || next > props.pageCount || next === props.page) return;
  emit("update:page", next);
}

function onSearchFiltersChanged(filters) {
  emit("filters-changed", filters);
}
</script>

<style scoped>
.ppt {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  background: #fff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow:
    0 1px 3px rgba(16, 24, 40, 0.1),
    0 1px 2px rgba(16, 24, 40, 0.06);
  overflow: hidden;
}

.ppt-filters {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
}

.ppt-asof {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px;
}

.ppt-filters__chips {
  display: flex;
  flex: 1 0 0;
  flex-wrap: wrap;
  gap: 12px;
  min-width: 0;
}

.ppt-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.ppt-chip--more svg {
  flex-shrink: 0;
}

.ppt-search {
  flex: 1 0 0;
  max-width: 320px;
  min-width: 0;
}

.ppt-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.ppt-table-wrap {
  width: 100%;
  overflow-x: auto;
}

.ppt-loading {
  border-top: 1px solid #eaecf0;
}

.ppt-error {
  padding: 48px 24px;
  text-align: center;
  border-top: 1px solid #eaecf0;
}

.ppt-error__title {
  margin: 0 0 8px;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.ppt-error__text {
  margin: 0 0 16px;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667085;
}

.ppt-error__retry {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}


.ppt-skeleton-row {
  display: grid;
  grid-template-columns: 30% 24% 24% 22%;
  gap: 16px;
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
}

.ppt-table {
  width: 100%;
  min-width: 980px;
  border-collapse: collapse;
  table-layout: fixed;
}

.ppt-col-employee {
  width: 24%;
}

/* 235px card + 36px cell indent + 48px cell padding, so the dates never truncate. */
.ppt-col-pay-plan {
  width: 320px;
}

.ppt-col-changes {
  width: 22%;
}

.ppt-col-status {
  width: 20%;
}

.ppt-table thead th {
  padding: 12px 24px;
  border-bottom: 1px solid #eaecf0;
  background: #f9fafb;
  text-align: left;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #667085;
  white-space: nowrap;
}

.ppt-th-with-icon {
  display: inline-flex;
  align-items: center;
  gap: 12px;
}

.ppt-th-icon {
  display: block;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
}

.ppt-table tbody td {
  padding: 16px 24px;
  border-bottom: 1px solid #eaecf0;
  vertical-align: middle;
}

.ppt-row--clickable {
  cursor: pointer;
}

.ppt-employee-cell,
.ppt-pay-plan-cell,
.ppt-changes-cell,
.ppt-status-cell {
  display: flex;
  align-items: center;
  min-width: 0;
  min-height: 40px;
}

.ppt-pay-plan-cell {
  padding-left: 36px;
}

.ppt-status-cell {
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  width: 100%;
}

.ppt-readiness {
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  text-align: left;
}

.ppt-empty {
  border-top: 1px solid #eaecf0;
}

.ppt-no-ranges {
  border-top: 1px solid #eaecf0;
}

.ppt-no-ranges__content {
  max-width: 560px;
  margin: 0 auto;
}

.ppt-empty-cta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.ppt-empty-cta:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.ppt-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px 16px;
  border-top: 1px solid #eaecf0;
}

.ppt-page-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
}

.ppt-page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ppt-page-numbers {
  display: flex;
  align-items: center;
  gap: 2px;
}

.ppt-page-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667085;
  cursor: pointer;
}

.ppt-page-num--active {
  background: #f9f5ff;
  color: #7f56d9;
}

.ppt-page-num--ellipsis {
  cursor: default;
}
</style>
